import React, {useContext, useEffect, useMemo, useState} from "react";
import "./SplashScreen.scss";
import DisplayLottie from "../../components/displayLottie/DisplayLottie";
import {greeting, splashScreen} from "../../portfolio";
import StyleContext from "../../contexts/StyleContext";

const STATUS_LINES = [
  "Initializing modules",
  "Loading portfolio",
  "Preparing experience",
  "Almost ready"
];

const PARTICLES = Array.from({length: 10}, (_, i) => ({
  id: i,
  left: `${8 + (i * 17) % 84}%`,
  top: `${6 + (i * 23) % 88}%`,
  size: 3 + (i % 3),
  delay: `${(i % 6) * 0.35}s`,
  duration: `${4 + (i % 4)}s`
}));

export default function SplashScreen() {
  const {isDark} = useContext(StyleContext);
  const durationMs = splashScreen.duration || 2200;
  const durationSec = durationMs / 1000;
  const [progress, setProgress] = useState(0);
  const [statusIndex, setStatusIndex] = useState(0);

  const particles = useMemo(
    () => (window.matchMedia("(max-width: 768px)").matches ? PARTICLES.slice(0, 6) : PARTICLES),
    []
  );

  useEffect(() => {
    let frame;
    const start = performance.now();

    const tick = now => {
      const t = Math.min(1, (now - start) / durationMs);
      const eased = 1 - Math.pow(1 - t, 1.6);
      setProgress(Math.round(eased * 100));
      if (t < 1) frame = requestAnimationFrame(tick);
    };

    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [durationMs]);

  useEffect(() => {
    const interval = Math.max(950, durationMs / STATUS_LINES.length);
    const timer = setInterval(() => {
      setStatusIndex(i => (i + 1) % STATUS_LINES.length);
    }, interval);
    return () => clearInterval(timer);
  }, [durationMs]);

  return (
    <div
      className={`splash-container${isDark ? " splash-dark" : " splash-light"}`}
      style={{"--splash-duration": `${durationSec}s`}}
    >
      <div className="splash-bg" aria-hidden="true">
        <div className="splash-aurora splash-aurora-1" />
        <div className="splash-aurora splash-aurora-2" />
        <span className="splash-blob splash-blob-1" />
        <span className="splash-blob splash-blob-2" />
        <span className="splash-blob splash-blob-3" />
        <div className="splash-grid" />
        <div className="splash-particles">
          {particles.map(p => (
            <span
              key={p.id}
              className="splash-particle"
              style={{
                left: p.left,
                top: p.top,
                width: p.size,
                height: p.size,
                animationDelay: p.delay,
                animationDuration: p.duration
              }}
            />
          ))}
        </div>
        <div className="splash-vignette" />
      </div>

      <div className="splash-content">
        <div className="splash-logo-wrap splash-item" style={{"--i": 0}}>
          <div className="splash-glow" aria-hidden="true" />
          <div className="splash-ring splash-ring-outer" aria-hidden="true" />
          <div className="splash-ring splash-ring-inner" aria-hidden="true" />
          <div className="splash-animation-container">
            <DisplayLottie animationData={splashScreen.animation} speed={2.4} />
          </div>
        </div>

        <div className="splash-title-container splash-item" style={{"--i": 1}}>
          <span className="splash-bracket splash-bracket-open">&lt;</span>
          <span className="splash-title">{greeting.username}</span>
          <span className="splash-bracket splash-bracket-close">/&gt;</span>
        </div>

        <p className="splash-subtitle splash-item" style={{"--i": 2}}>
          Full-Stack Developer
        </p>

        <div className="splash-tags splash-item" style={{"--i": 3}} aria-hidden="true">
          <span>Nuxt.js</span>
          <span>Node.js</span>
          <span>AWS</span>
        </div>

        <div className="splash-progress-wrap splash-item" style={{"--i": 4}}>
          <div className="splash-progress-meta">
            <span className="splash-loading-text" key={statusIndex}>
              {STATUS_LINES[statusIndex]}
              <span className="splash-dots" aria-hidden="true">
                <span>.</span>
                <span>.</span>
                <span>.</span>
              </span>
            </span>
            <span className="splash-percent">{progress}%</span>
          </div>
          <div className="splash-progress-track">
            <div
              className="splash-progress-fill"
              style={{transform: `scaleX(${progress / 100})`}}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
